import { ITicket, IPagination, ITicketTotal } from '@/utils/index.model';
import { getData, keysToCamel } from '@/utils/helpers';
import { normalizeTickets } from '@/utils/ticket-selections';
import { API_URLS } from '@/utils/constant';

/*
 * The odds a cockfight bet was placed at, as the bet zone showed them. The
 * tickets endpoint returns `odds: 0` until the ticket settles, so the rail
 * reads these for a bet still running. Keyed `${fightId}:${selection}`, a list
 * because one selection can be bet several times in a round at different odds.
 * Kept in sessionStorage so a reload mid-round keeps them.
 */
export interface IPlacedOdds {
  at: number;
  odds: number;
}
type PlacedOddsMap = { [key: string]: IPlacedOdds[] };

// `status` on the tickets endpoint: 4 is settled.
const TICKET_STATUS_SETTLED = 4;

const PLACED_ODDS_KEY = 'ga28.placedOdds';
// Longer than any round; older entries are dropped so the map never grows.
const PLACED_ODDS_TTL_MS = 60 * 60 * 1000;

const prunePlacedOdds = (map: PlacedOddsMap): PlacedOddsMap => {
  const cutoff = Date.now() - PLACED_ODDS_TTL_MS;
  return Object.keys(map).reduce((kept: PlacedOddsMap, key) => {
    const entries = (map[key] || []).filter((entry) => entry && entry.at > cutoff);
    if (entries.length) kept[key] = entries;
    return kept;
  }, {});
};

const readPlacedOdds = (): PlacedOddsMap => {
  try {
    return prunePlacedOdds(JSON.parse(window.sessionStorage.getItem(PLACED_ODDS_KEY) || '{}'));
  } catch (error) {
    return {};
  }
};

const writePlacedOdds = (map: PlacedOddsMap): void => {
  try {
    window.sessionStorage.setItem(PLACED_ODDS_KEY, JSON.stringify(map));
  } catch (error) {
    // Private mode, full storage: the odds still hold for this page view.
  }
};

export const placedOddsKey = (fightId: string, selection: string): string =>
  `${fightId}:${String(selection || '').toLowerCase()}`;

interface IHistoryParams {
  from_bet_date_time: string;
  to_bet_date_time: string;
  game_type?: number;
}

// Debounce for history syncs: every table ticks its own round, so triggers burst.
const HISTORY_SYNC_DEBOUNCE_MS = 1000;
// The tickets endpoint can settle a bet a moment after its round reports a result.
const HISTORY_SYNC_RETRY_MS = 4000;
let historySyncTimer: any = null;
let historyRetryTimer: any = null;

const isSameFilter = (a: IHistoryParams | null, b: IHistoryParams | null): boolean =>
  !!a &&
  !!b &&
  a.from_bet_date_time === b.from_bet_date_time &&
  a.to_bet_date_time === b.to_bet_date_time &&
  a.game_type === b.game_type;

interface ITicketState {
  // Filter of the Bet History tab while it is open; null when it is closed.
  historyParams: IHistoryParams | null;
  ticketListing: ITicket[];
  // The desktop rail's CURRENT list. Separate from `ticketListing` on purpose:
  // that one is driven by the history tab's own date filter and pagination, so
  // the two would overwrite each other.
  recentTickets: ITicket[];
  // The rail's CURRENT list starts empty and only fills once the member bets
  // in this page view; until then a round change does not fetch it either.
  hasPlacedBet: boolean;
  placedOdds: PlacedOddsMap;
  ticketTotals: ITicketTotal[];
  ticketLoading: boolean;
  ticketPagination: IPagination;
  selectedTicket: ITicket;
}

const TICKET_DATA: ITicket = {
  id: '',
  createdDateTime: '',
  selection: '',
  odds: 0,
  amount: 0,
  result: '',
  winLossAmount: 0,
  taxAmount: 0,
  fight: {
    id: '',
    fightNumber: 0,
    status: '',
    result: '',
    streamUrl: '',
    replayUrl: '',
    isBetsPaused: false,
    confirmedDateTime: '',
    selections: [
      {
        name: '',
        displayName: '',
        odds: 0,
        totalBetAmount: 0,
        totalBetCount: 0,
      },
    ],
    matchDay: {
      id: '',
      code: '',
      startDatetime: '',
      fightType: '',
      arena: {
        id: '',
        name: {
          default: '',
          vn: '',
        },
        country: '',
        code: '',
        currentMatchDayId: '',
        isReverse: false,
      },
    },
  },
};

export const state = () => ({
  historyParams: null as IHistoryParams | null,
  ticketListing: [TICKET_DATA],
  recentTickets: [],
  hasPlacedBet: false,
  placedOdds: readPlacedOdds(),
  ticketTotals: [],
  ticketLoading: false,
  ticketPagination: { page: 1, pageSize: 20 },
  selectedTicket: {} as ITicket,
});

export const mutations = {
  INIT_RECENT_TICKETS(stateData: ITicketState, tickets: ITicket[]) {
    // CURRENT keeps its own layout: no "N BETS" built from a selection summary.
    stateData.recentTickets = normalizeTickets(tickets, false);
  },
  ADD_PLACED_ODDS(stateData: ITicketState, payload: { fightId: string; odds: { [selection: string]: number } }) {
    const at = Date.now();
    const map = prunePlacedOdds({ ...stateData.placedOdds });
    Object.keys(payload.odds).forEach((selection) => {
      const key = placedOddsKey(payload.fightId, selection);
      map[key] = [...(map[key] || []), { at, odds: payload.odds[selection] }];
    });
    stateData.placedOdds = map;
    writePlacedOdds(map);
  },
  SET_HISTORY_PARAMS(stateData: ITicketState, params: IHistoryParams | null) {
    stateData.historyParams = params ? { ...params } : null;
  },
  SET_HAS_PLACED_BET(stateData: ITicketState, value: boolean) {
    stateData.hasPlacedBet = value;
  },
  INIT_TICKETS_TOTALS(stateData: ITicketState, ticketTotals: ITicketTotal[]) {
    stateData.ticketTotals = [...ticketTotals];
  },
  INIT_TICKETS(stateData: ITicketState, tickets: ITicket[]) {
    stateData.ticketListing = normalizeTickets(tickets);
  },
  INSERT_TICKETS(stateData: ITicketState, tickets: ITicket[]) {
    stateData.ticketListing = [...stateData.ticketListing, ...normalizeTickets(tickets)];
  },
  RESET_TICKETS(stateData: ITicketState) {
    stateData.ticketListing = [];
  },
  UPDATE_PAGINATION(stateData: ITicketState, pagination: IPagination): void {
    stateData.ticketPagination = { ...pagination };
  },
  RESET_PAGINATION(stateData: ITicketState) {
    stateData.ticketPagination = { page: 1, pageSize: 20 };
  },
  UPDATE_LOADING(stateData: ITicketState, loading: boolean): void {
    stateData.ticketLoading = loading;
  },
  UPDATE_TICKET(stateData: ITicketState, ticket: ITicket) {
    stateData.selectedTicket = { ...ticket };
  },
  RESET_TICKET(stateData: ITicketState) {
    stateData.selectedTicket = {} as ITicket;
  },
};

export const actions = {
  resetTickets(context: any): void {
    context.commit('RESET_TICKETS');
  },
  getTickets(
    context: any,
    paramsPartial: {
      status?: string;
      from_bet_date_time: string;
      to_bet_date_time: string;
      // Product filter (ARENA_GAME_TYPE); the endpoint's `game_type`.
      game_type?: number;
    }
  ): void {
    const { state, rootGetters } = context;
    const token = rootGetters['general/token'];
    const { ticketPagination } = state;
    const oddsType = rootGetters['general/oddsType'];
    const params = new URLSearchParams();
    const { from_bet_date_time, to_bet_date_time, game_type } = paramsPartial;
    params.append('from_bet_date_time', from_bet_date_time);
    params.append('to_bet_date_time', to_bet_date_time);
    params.append('page', ticketPagination.page);
    params.append('page_size', ticketPagination.pageSize);
    params.append('odds_type', oddsType);
    if (game_type !== undefined) params.append('game_type', String(game_type));

    // params.append("status", "approved");
    // params.append("status", "processed");
    // params.append("status", "cancelled");
    // params.append("status", "hold");

    context.commit('UPDATE_LOADING', true);
    getData(API_URLS.BET_HISTORY, token, params)
      .then((res) => {
        if (ticketPagination.page === 1) {
          context.commit('INIT_TICKETS', keysToCamel(res));
        } else {
          context.commit('INSERT_TICKETS', keysToCamel(res));
        }
        context.commit('UPDATE_PAGINATION', {
          page: ticketPagination.page + 1,
          pageSize: ticketPagination.pageSize,
        });
      })
      .catch((e) => console.log(e))
      .finally(() => context.commit('UPDATE_LOADING', false));
  },
  /*
   * The newest tickets for the desktop rail, newest first.
   *
   * Deliberately its own call rather than a slice of `getTickets`: that one
   * carries the history tab's filters and advances a shared page counter, so
   * reusing it would make the two lists fight. `limit` is small because the
   * rail only ever shows a handful.
   */
  getRecentTickets(
    context: any,
    { from, to, limit = 10 }: { from: string; to: string; limit?: number }
  ): Promise<ITicket[]> {
    const token = context.rootGetters['general/token'];
    if (!token) return Promise.resolve([]);
    const oddsType = context.rootGetters['general/oddsType'];

    const params = new URLSearchParams();
    params.append('from_bet_date_time', from);
    params.append('to_bet_date_time', to);
    params.append('page', '1');
    params.append('page_size', String(limit));
    params.append('odds_type', oddsType);

    return getData(API_URLS.BET_HISTORY, token, params)
      .then((res) => {
        const tickets = keysToCamel(res);
        context.commit('INIT_RECENT_TICKETS', tickets);
        return tickets;
      })
      .catch((e) => {
        console.log(e);
        return [];
      });
  },
  /*
   * The rail's window: the last day, a little deeper than the five rows it
   * shows. Lives here rather than in the rail so a bet submit can refresh the
   * list too -- otherwise a new bet only showed up on the next round change.
   */
  refreshRecentTickets(context: any): Promise<ITicket[]> {
    const now = Date.now();
    const from = new Date(now - 24 * 60 * 60 * 1000);
    /*
     * An hour ahead, not "now": the bet's time is the server's, and with the
     * client's clock even a few seconds behind, a bet placed a moment ago fell
     * after the window and only showed on a later refetch.
     */
    const to = new Date(now + 60 * 60 * 1000);
    return context.dispatch('getRecentTickets', {
      // ISO 8601, which is what the endpoint accepts -- a space-separated
      // datetime is rejected with a 400.
      from: from.toISOString(),
      to: to.toISOString(),
      limit: 10,
    });
  },
  // A cockfight bet was accepted at these odds (see IPlacedOdds).
  rememberPlacedOdds(context: any, payload: { fightId: string; odds: { [selection: string]: number } }): void {
    if (!payload.fightId || !Object.keys(payload.odds || {}).length) return;
    context.commit('ADD_PLACED_ODDS', payload);
  },
  /*
   * On load: `hasPlacedBet` is in-memory, so a reload lost it and the rail
   * stayed empty -- and never refetched -- until the next bet, even with bets
   * still running. One fetch brings those back; an unsettled ticket among them
   * opens the gate so round changes keep the list in step.
   */
  restoreRecentTickets(context: any): Promise<void> {
    return context.dispatch('refreshRecentTickets').then((tickets: ITicket[]) => {
      const hasRunningBet = (tickets || []).some(
        (ticket: any) =>
          ticket &&
          Number(ticket.status) !== TICKET_STATUS_SETTLED &&
          !['cancelled', 'void'].includes(String(ticket.result || '').toLowerCase())
      );
      if (hasRunningBet) context.commit('SET_HAS_PLACED_BET', true);
    });
  },
  // After a Tài Xỉu or cockfight bet is accepted: show it on the CURRENT list.
  refreshAfterBet(context: any): void {
    context.commit('SET_HAS_PLACED_BET', true);
    context.dispatch('refreshRecentTickets');
    context.dispatch('syncHistory');
  },
  // The Bet History tab reports (or clears, with null) the filter it is showing.
  setHistoryParams(context: any, params: IHistoryParams | null): void {
    context.commit('SET_HISTORY_PARAMS', params);
    if (!params) {
      clearTimeout(historySyncTimer);
      clearTimeout(historyRetryTimer);
    }
  },
  /*
   * Keep an open Bet History tab current after a bet or a settlement, without
   * the member refreshing. Debounced, then once more after a delay in case the
   * backend settled the ticket just after the trigger. No-op when the tab is
   * closed -- it loads fresh on open anyway.
   */
  syncHistory(context: any): void {
    if (!context.state.historyParams) return;
    clearTimeout(historySyncTimer);
    clearTimeout(historyRetryTimer);
    historySyncTimer = setTimeout(() => {
      context.dispatch('reloadHistory');
      historyRetryTimer = setTimeout(() => context.dispatch('reloadHistory'), HISTORY_SYNC_RETRY_MS);
    }, HISTORY_SYNC_DEBOUNCE_MS);
  },
  /*
   * Refetch what the tab already shows (page 1 up to the rows loaded) and swap
   * it in place, leaving pagination alone so "Load more" and the scroll
   * position survive.
   */
  reloadHistory(context: any): void {
    const { state, rootGetters } = context;
    const filter = state.historyParams;
    const token = rootGetters['general/token'];
    // A page load in flight is as fresh as this would be.
    if (!filter || !token || state.ticketLoading) return;
    const { pageSize, page } = state.ticketPagination;
    const loaded = Math.max(pageSize, pageSize * (page - 1));

    const params = new URLSearchParams();
    params.append('from_bet_date_time', filter.from_bet_date_time);
    params.append('to_bet_date_time', filter.to_bet_date_time);
    params.append('page', '1');
    params.append('page_size', String(loaded));
    params.append('odds_type', rootGetters['general/oddsType']);
    if (filter.game_type !== undefined) params.append('game_type', String(filter.game_type));

    getData(API_URLS.BET_HISTORY, token, params)
      .then((res) => {
        // The tab may have closed or switched filter while this was in flight.
        if (context.state.historyParams !== filter && !isSameFilter(context.state.historyParams, filter)) return;
        context.commit('INIT_TICKETS', keysToCamel(res));
        context.dispatch('getTicketTotals', filter);
      })
      .catch((e) => console.log(e));
  },
  getTicketTotals(
    context: any,
    paramsPartial: {
      status?: string;
      from_bet_date_time: string;
      to_bet_date_time: string;
      // Product filter (ARENA_GAME_TYPE); the endpoint's `game_type`.
      game_type?: number;
    }
  ): void {
    const { rootGetters } = context;
    const token = rootGetters['general/token'];
    const oddsType = rootGetters['general/oddsType'];
    const timezone = rootGetters['general/timezone'];
    const params = new URLSearchParams();
    const { from_bet_date_time, to_bet_date_time, game_type } = paramsPartial;
    params.append('from_bet_date_time', from_bet_date_time);
    params.append('to_bet_date_time', to_bet_date_time);
    params.append('odds_type', oddsType);
    params.append('timezone', timezone);
    if (game_type !== undefined) params.append('game_type', String(game_type));

    // params.append("status", "approved");
    // params.append("status", "processed");
    // params.append("status", "cancelled");
    // params.append("status", "hold");

    getData(API_URLS.BET_HISTORY_TOTAL, token, params)
      .then((res) => {
        context.commit('INIT_TICKETS_TOTALS', keysToCamel(res));
      })
      .catch((e) => console.log(e));
  },
  resetTicketsPage(context: any): void {
    context.commit('RESET_PAGINATION');
  },
  updateTicket(context: any, ticket: ITicket): void {
    context.commit('UPDATE_TICKET', ticket);
  },
  resetTicket(context: any): void {
    context.commit('RESET_TICKET');
  },
  resetTicketPage(context: any): void {
    context.commit('RESET_PAGINATION');
  },
};

export const getters = {
  recentTickets: (stateData: any): ITicket[] => {
    return stateData.recentTickets;
  },
  hasPlacedBet: (stateData: any): boolean => stateData.hasPlacedBet,
  placedOdds: (stateData: any): PlacedOddsMap => stateData.placedOdds,
  ticketListing: (stateData: any): ITicket[] => {
    return stateData.ticketListing;
  },
  ticketTotals: (stateData: any): ITicketTotal[] => {
    return stateData.ticketTotals;
  },
  ticketLoading: (stateData: any): boolean => {
    return stateData.ticketLoading;
  },
  ticketPagination: (stateData: any): IPagination => {
    return stateData.ticketPagination;
  },
  currentTicket: (stateData: any): ITicket => {
    return stateData.selectedTicket;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
